// -----------------------------------------------------------------------------
// taskAssigneeDelta — turn "here is the new selection" into "add these, remove
// those", for single-task assignee writes.
//
// Dependency-free on purpose (no React, no MUI, no imports) so its suite runs
// in seconds. Anything that pulls in the components barrel takes minutes to
// start in this repo, which in practice means it never gets run.
//
// WHY THIS EXISTS
//   TaskAssigneeHeads persisted every avatar click by PATCHing the WHOLE
//   assignee list, which the API treats as replace semantics — anyone absent
//   from the array is deleted. That is only safe if the array is always
//   complete, and it is not:
//
//     * the picker seeds from `assignableUsers`, which EXCLUDES ADMIN, so a
//       task whose only row is the admin placeholder hydrates as EMPTY — and
//       70 of practice 19's 71 unassigned tasks are exactly that shape; and
//     * the component's local selection resets, while `persist` never
//       reconciles against the server.
//
//   So a click computed from an empty base sent a ONE-element array and
//   deleted everyone else. Prod, 30 days, practice 19: 1,476 add-then-remove
//   events within 120s across 782 tasks, the same actor on both sides — the
//   UI overwriting itself. Lead 190927 "First Contact":
//
//     added ['98']         removed ['19']      add Sophie, delete the admin row
//     added ['1279']       removed ['98']      add Tia,    delete Sophie
//     added ['98','1406']  removed ['1279']    add Sophie+Jess, delete Tia
//
//   THE POINT OF A DELTA: a stale base can no longer cause a deletion. If the
//   picker wrongly believes nobody is assigned and you click one person, the
//   diff is {added:[them], removed:[]} — they are added and everyone already
//   on the task survives, admin row included. The worst case degrades from
//   "silently deleted seven people" to "you may need to click again".
// -----------------------------------------------------------------------------

/**
 * Normalise a picker entry to its PracticeUser id as a string.
 *
 * The id arrives as a number from usePracticeStaff and as a string from the
 * API, and AssignedToLead already coerces both sides for the same reason —
 * a `===` mismatch here would report a person as both added and removed.
 */
function toId(entry) {
    if (entry === null || entry === undefined) return null;
    const raw = typeof entry === 'object' ? entry.id : entry;
    if (raw === null || raw === undefined || raw === '') return null;
    return String(raw);
}

/** Unique, non-null ids from a selection array, order preserved. */
export function toIdSet(selection) {
    const out = [];
    const seen = new Set();
    (selection || []).forEach((entry) => {
        const id = toId(entry);
        if (id === null || seen.has(id)) return;
        seen.add(id);
        out.push(id);
    });
    return out;
}

/**
 * What changed between the previous selection and the next one.
 *
 * @returns {{added: string[], removed: string[]}} PracticeUser ids as strings.
 */
export function diffAssignees(prevSelection, nextSelection) {
    const prev = toIdSet(prevSelection);
    const next = toIdSet(nextSelection);
    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    return {
        added: next.filter((id) => !prevSet.has(id)),
        removed: prev.filter((id) => !nextSet.has(id)),
    };
}

/** True when the diff would write nothing — skip the request entirely. */
export function isNoopDelta(delta) {
    return !delta || (delta.added.length === 0 && delta.removed.length === 0);
}

/**
 * Did bulk-assignees decline this task because it is not OPEN?
 *
 * apps/tasks/views.py::bulk_assignees only mutates pending / in_progress
 * tasks; anything else is reported as skipped with reason "task_<status>"
 * (e.g. task_completed, task_archived) and NOT as an error. The single-task
 * PATCH has never had that restriction, so a caller replacing PATCH with this
 * endpoint must detect the skip and fall back — otherwise assigning on a
 * completed task silently becomes a no-op, which would be a NEW bug.
 *
 * "not_found" and "no_permission" are deliberately NOT treated as fallback
 * cases: those are real failures and must surface to the operator.
 */
export function getSkipReason(responseData, taskId) {
    const skipped = responseData?.skipped;
    if (!Array.isArray(skipped) || skipped.length === 0) return null;
    const wanted = toId(taskId);
    const row = skipped.find((entry) => {
        if (typeof entry?.reason !== 'string') return false;
        const rowId = toId(entry?.task_id);
        return wanted === null || rowId === null || rowId === wanted;
    });
    return row ? row.reason : null;
}

export function isSkippedForClosedStatus(responseData, taskId) {
    const reason = getSkipReason(responseData, taskId);
    return typeof reason === 'string' && reason.startsWith('task_');
}

/**
 * A skip we must NOT paper over: the task is gone, or this user may not touch
 * it. Silently reporting success on either would hide a real failure — the
 * caller has to surface these, not fall back.
 */
export function skipMessage(reason) {
    if (reason === 'not_found') return 'That task no longer exists.';
    if (reason === 'no_permission') return 'You do not have permission to assign this task.';
    return null;
}

/**
 * The write plan for one delta: at most two calls, adds FIRST.
 *
 * Order matters. If the second call fails, adds-first leaves the task
 * OVER-assigned rather than under-assigned — and under-assignment is the
 * defect being fixed here, while an extra assignee is harmless.
 *
 * @returns {{mode: 'add'|'remove', userIds: string[]}[]}
 */
export function buildWritePlan(delta) {
    const plan = [];
    if (delta?.added?.length) plan.push({ mode: 'add', userIds: delta.added });
    if (delta?.removed?.length) plan.push({ mode: 'remove', userIds: delta.removed });
    return plan;
}
